import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from '@mui/material';
import list from 'assets/icons/list.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { getPixMask } from 'utils/get-pix-mask';
import { useStoreSelector } from 'v3/data/store';
import { clearSellerSelected } from 'v3/data/store/seller/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { TextMaskPhone } from 'v3/shared/components/Inputs/MaskPhoneInput';
import { SelectRadioFormController } from 'v3/shared/components/Inputs/SelectRadioFormController';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { RoleEnum } from 'v3/shared/enums/RoleEnum';
import { createSellerSchema } from 'v3/shared/schemas/createSeller';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleMedium } from 'v3/shared/typography/TitleMedium';
import { theme } from 'v3/styles/global-styles';
import {
  AlignButtons,
  Container,
  Content,
  CustomPaper,
  FiltersContainer,
  FormSection,
  Header,
  SectionContent,
  StyledCloseIcon,
} from './styles';
import { IChangeValueModalProps, ICreateSellerForm } from './types';

const getStoreOptions = (stores: Array<{ id: string; name: string }>) =>
  stores.map((store) => ({ key: store.name, value: store.id }));

const getInitialPhoneNumber = (initialState): string => {
  if (!initialState || !initialState.phoneAreaCode || !initialState.phoneNumber) return '';
  return `${initialState.phoneAreaCode}${initialState.phoneNumber}`;
};

const extractPhoneParts = (phone?: string) => {
  const digits = (phone || '').replace(/\D/g, '');
  return {
    phoneAreaCode: digits.slice(0, 2),
    phoneNumber: digits.slice(2),
  };
};

export const SellerFormModal: React.FC<IChangeValueModalProps> = ({ modal, onClose, onConfirm }) => {
  const { isMobile } = useBreakpoints();
  const { storeList } = useStoreSelector((store) => store.seller);

  const [sellerBankAccountFeatureEnabled, setSellerBankAccountFeatureEnabled] = useState(false);

  const schema = useMemo(() => createSellerSchema(sellerBankAccountFeatureEnabled), [sellerBankAccountFeatureEnabled]);

  const {
    control,
    getValues,
    setValue,
    formState: { isValid },
    reset,
    clearErrors,
  } = useForm<ICreateSellerForm>({
    values: {
      name: modal.initialState?.name || '',
      code: modal.initialState?.code || '',
      email: modal.initialState?.email || '',
      phoneNumber: getInitialPhoneNumber(modal.initialState),
      storeId: modal.initialState?.storeId || '',
      role: modal.initialState?.role || '',
      storeName: modal.initialState?.store?.name || '',
      pixKey: modal.initialState?.pixKey || '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema) as any,
  });

  const pixKey = useWatch({ name: 'pixKey', control });
  const selectedStoreName = useWatch({ name: 'storeName', control });

  useEffect(() => {
    const selectedStore = storeList.find((store) => store.name === selectedStoreName);

    if (selectedStore) {
      setValue('storeId', selectedStore.id);
    }
  }, [selectedStoreName, storeList, setValue]);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const featureBackofficeResponse = await api.getFlagStatus(
      FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_BACKOFFICE,
    );

    const isEnabled = Boolean(featureBackofficeResponse?.data?.enabled ?? featureBackofficeResponse?.data);

    setSellerBankAccountFeatureEnabled(isEnabled);
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, [fetchSellerFeatureFlag]);

  const handleClose = () => {
    onClose();
    reset();
    clearSellerSelected();
    clearErrors();
  };

  const handleSave = () => {
    const formValues = getValues();

    const basePayload = {
      name: formValues.name.trim(),
      code: formValues.code,
      storeId: formValues.storeId,
    };

    let payload: any = { ...basePayload };

    if (sellerBankAccountFeatureEnabled) {
      const { phoneAreaCode, phoneNumber } = extractPhoneParts(formValues.phoneNumber);

      payload = {
        ...basePayload,
        role: formValues.role,
        email: formValues.email,
        pixKey: formValues.pixKey,
        phoneAreaCode,
        phoneNumber,
      };
    }

    const updatedValues = {
      ...modal.initialState,
      ...payload,
    };

    onConfirm(updatedValues, reset);
  };

  return (
    <Dialog
      onClose={handleClose}
      open={modal.active}
      aria-labelledby="customized-dialog-title"
      PaperComponent={CustomPaper}
    >
      <Container>
        <FiltersContainer>
          <Header>
            <img src={list} alt="list" />
            <SubtitleLarge>{modal.title}</SubtitleLarge>
            <StyledCloseIcon onClick={handleClose} />
          </Header>
        </FiltersContainer>

        <Content>
          <FormSection>
            <TitleMedium color={theme.palette.common.white}>Dados pessoais</TitleMedium>
            <SectionContent>
              <InputFormController
                data-testid="input-jazzStoreId"
                formControl={control}
                formControlName="name"
                label="Nome"
              />
              <InputFormController
                formControl={control}
                inputType="tel"
                formControlName="code"
                label="CPF"
                mask="000.000.000-00"
                placeholder="___.___.___-__"
                customMask={TextMaskCustom}
              />
            </SectionContent>

            {sellerBankAccountFeatureEnabled && (
              <>
                <InputFormController
                  data-testid="input-email"
                  formControl={control}
                  formControlName="email"
                  label="E-mail"
                />

                <InputFormController
                  data-testid="input-phone-number"
                  formControl={control}
                  formControlName="phoneNumber"
                  label="Número de celular"
                  mask="(00)00000-0000"
                  placeholder="(__)_____-____"
                  customMask={TextMaskPhone}
                  inputType="tel"
                />
              </>
            )}
          </FormSection>

          <FormSection>
            <TitleMedium color={theme.palette.common.white}>Dados da loja</TitleMedium>
            <SectionContent>
              <SelectRadioFormController
                data-testid="input-store"
                formControl={control}
                formControlName="storeName"
                label="Lista de lojas"
                setValue={setValue}
                defaultValues={getStoreOptions(storeList)}
              />
              {sellerBankAccountFeatureEnabled && (
                <DropdownSelectFormController
                  data-testid="input-role"
                  formControl={control}
                  formControlName="role"
                  label="Cargo"
                  enumType
                  selectValues={RoleEnum}
                  onChangeSelect={() => {}}
                />
              )}
            </SectionContent>
          </FormSection>

          {sellerBankAccountFeatureEnabled && (
            <FormSection>
              <TitleMedium color={theme.palette.common.white}>Forma de repasse</TitleMedium>
              <SectionContent>
                <InputFormController
                  data-testid="input-pix-key"
                  formControl={control}
                  formControlName="pixKey"
                  label="Chave Pix"
                  placeholder="CPF, e-mail, telefone ou chave aleatória"
                  mask={getPixMask(pixKey as string)}
                  customMask={TextMaskCustom}
                />
              </SectionContent>
            </FormSection>
          )}

          <AlignButtons>
            <FlexButton
              data-testid="flex-button-save-button"
              size="small"
              text={modal.saveButton}
              handleClick={handleSave}
              fullWidth={isMobile}
              disabled={!isValid}
            />
            <TextButton
              data-testid="text-button-cancel"
              size="small"
              $outline
              text={modal.cancelButton}
              handleClick={handleClose}
              fullWidth={isMobile}
            />
          </AlignButtons>
        </Content>
      </Container>
    </Dialog>
  );
};
